import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiUsers, FiArrowRight, FiUserPlus, FiMail, FiSettings,
  FiCheckCircle, FiMessageSquare, FiFolder
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const PersonalTeams = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/personal/teams" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/personal" className="hover:text-blue-600">Personal Workspace</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Personal Teams</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl mb-4">
              <FiUsers className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Personal Teams</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Create and manage small teams for collaboration without the overhead of organization structures.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">What are Personal Teams?</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Personal Teams allow you to collaborate with others on projects without creating a 
                full organization. Perfect for freelancers, small groups, or temporary collaborations.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Best For</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Small project teams (2-10 members)</li>
                    <li>• Freelancer collaborations</li>
                    <li>• Temporary working groups</li>
                    <li>• Personal project sharing</li>
                  </ul>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Key Features</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Shared projects and tasks</li>
                    <li>• Simple member management</li>
                    <li>• Real-time collaboration</li>
                    <li>• Activity feeds</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Creating a Team */}
          <section className="mb-16" id="create-team">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Creating a Team</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Navigate to Teams</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">From your dashboard, go to "Teams" in the sidebar</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Click "Create Team"</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Enter a team name and optional description</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Invite Members</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Add team members via email invitation</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Managing Members */}
          <section className="mb-16" id="manage-members">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Managing Team Members</h2>
            
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiUserPlus className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Inviting Members</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Send email invitations to add new members. They'll receive a link to join your team.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiSettings className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Member Roles</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Assign roles: <strong>Admin</strong> (full control) or <strong>Member</strong> (collaborate on tasks).
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiMail className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Pending Invitations</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  View and manage pending invitations. Resend or cancel invites as needed.
                </p>
              </div>
            </div>
          </section>

          {/* Team Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Team Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <FiFolder className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Shared Projects</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Create projects visible to all team members. Collaborate on tasks together.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <FiMessageSquare className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Team Activity</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  See what your team is working on with the shared activity feed.
                </p>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/personal/projects"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Project Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default PersonalTeams;
